import { isStringDecimalNumber } from 'src/common/helpers/validators/isStringDecimalNumber'
import { stripCharacters } from 'src/common/helpers/sanitisers/stripCharacters'
import {
  getCurrencyByName,
  convertNumberToFixed,
  sanitiseValue,
  getCurrencyCharactersToRemove,
  addThousandsPoints,
  isCurrencyValid,
} from 'src/common/helpers/generator/monetaryAmount.helper'
import { DirectiveBinding, VNode } from 'vue'
import { removeDigit, appendDigit } from 'src/common/helpers/generator/number.helper'
import { isCharNumber } from 'src/common/helpers/validators/isCharNumber'

interface ICurrency {
  decimal: string
  thousands: string
  precision: number
  prefix: string
  suffix: string
}

interface IState {
  currency: ICurrency
  currencyName: string
  model: string
  numberValue: number
  value: string
  [key: string]: string | number | ICurrency
}

interface IElement extends HTMLElement {
  state: IState
}

interface IVNode extends VNode {
  ctx: VNode & { emitsOptions: Record<string, unknown>; emit: (name: string, data: unknown) => void }
}

/* -------------------------------- Constants ------------------------------- */

// The proxy object definition used for handling the price format
const handler = {
  get(target: IState, prop: string) {
    return target[prop]
  },
  set(obj: IState, prop: string, value: string | ICurrency) {
    if (prop === 'value') {
      const charactersToRemove = getCurrencyCharactersToRemove(obj.currencyName)
      let stringValue = stripCharacters(value.toString().trim(), charactersToRemove)
      if (!isStringDecimalNumber(stringValue, obj.currency.decimal)) {
        stringValue = '0'
      }
      const numberValue = Number(stringValue.replace(obj.currency.decimal, '.'))
      obj.numberValue = numberValue

      const sanitisedValue = sanitiseValue(stringValue, numberValue, obj.currencyName)
      obj.value = addThousandsPoints(sanitisedValue, obj.currencyName)

      obj.model = `${obj.currency.prefix}${obj.value}${obj.currency.suffix}`
    } else {
      obj[prop] = value
    }
    return true
  },
}

/* -------------------------------- Functions ------------------------------- */

/**
 * Creates the proxy object with the definitions defined inside the 'handler' variable
 */
function createState(): IState {
  return new Proxy<IState>(
    { model: '', value: '0', numberValue: 0, currency: {} as ICurrency, currencyName: '' },
    handler,
  )
}

/**
 * Uses source element's events to emit the number value and the formatted value
 * (This causes the 'updated' directive hook to be triggered)
 */
function emit(vnode: IVNode, data: string | number, property = 'modelValue') {
  vnode.ctx.emit(`update:${property}`, data)
}

/**
 * Initialises the input value with 0 and returns the input element
 */
function initialiseValue(el: HTMLElement, vnode: IVNode): HTMLInputElement {
  const input = el.querySelector('.q-field__native') as HTMLInputElement
  if (input.value === '') emit(vnode, '0')
  return input
}

/**
 * Handles the logic for different key presses including number keys, alphabetical keys, backspace, and sign keys
 */
function handleKeyboardLogic(e: KeyboardEvent, vnode: IVNode, signed: boolean, state: IState) {
  const key = e.key
  const value = vnode.ctx.props?.modelValue
  const currency = getCurrencyByName(state.currencyName)
  let shouldUpdate = true
  if (key === 'Backspace' || isCharNumber(key)) {
    state.value = value
    let updatedValue = 0
    const precision = currency?.precision ?? 2
    if (key === 'Backspace') {
      state.value = state.value.slice(0, -1)
      updatedValue = removeDigit(Math.abs(state.numberValue), precision)
    } else updatedValue = appendDigit(Math.abs(state.numberValue), key, precision)
    const signMultiplier = state.numberValue < 0 ? -1 : 1
    const decimalValue = String(updatedValue * signMultiplier).replace('.', currency.decimal)
    state.value = convertNumberToFixed(decimalValue, state.currencyName)
  } else if (signed && (key === '+' || key === '-')) {
    state.value = value
    let updatedValue = String(Math.abs(state.numberValue)).replace('.', currency.decimal)
    if (key === '-') updatedValue = String(-Math.abs(state.numberValue)).replace('.', currency.decimal)
    state.value = convertNumberToFixed(updatedValue, state.currencyName)
  } else shouldUpdate = false

  if (shouldUpdate) {
    emit(vnode, state.model)
    emit(vnode, state.numberValue, 'value')
  }
  e.preventDefault()
}

function handleSelectionLogic(e: KeyboardEvent, vnode: IVNode, signed: boolean, state: IState) {
  state.value = convertNumberToFixed('0', state.currencyName)
  e.preventDefault()
  emit(vnode, state.model)
  emit(vnode, state.numberValue, 'value')
  setTimeout(() => handleKeyboardLogic(e, vnode, signed, state))
}

/* -------------------------------- Directive ------------------------------- */

export default {
  mounted(el: IElement, binding: DirectiveBinding, vnode: IVNode | any) {
    const input = initialiseValue(el, vnode)
    const state = createState()
    // Add a custom emit option to allow emitting the number value
    vnode.ctx.emitsOptions['update:value'] = null
    // Save the state proxy object on the element to prevent conflict with other inputs that use the same directive
    el.state = state

    state.currency = getCurrencyByName(binding.value)
    if (isCurrencyValid(binding.value)) state.currencyName = binding.value
    else state.currencyName = 'GBP'

    const modelValue = vnode.ctx.props?.modelValue.toString().replace('.', state.currency.decimal)
    state.value = convertNumberToFixed(modelValue, state.currencyName)
    emit(vnode, state.model)
    emit(vnode, state.numberValue, 'value')

    input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (input.selectionStart === input.selectionEnd)
        handleKeyboardLogic(e, vnode, binding.modifiers.signed ?? false, state)
      else handleSelectionLogic(e, vnode, binding.modifiers.signed ?? false, state)
    })
  },

  updated(el: IElement, _binding: DirectiveBinding, vnode: IVNode | any) {
    const state = el.state
    if (vnode.ctx.props.modelValue === '') {
      state.value = convertNumberToFixed('0', state.currencyName)
      emit(vnode, state.model)
      emit(vnode, state.numberValue, 'value')
    }
  },
}
